import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import { useAuth } from './AuthContext';

const PlanContext = createContext();

export function usePlan() {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('usePlan must be used within PlanProvider');
  }
  return context;
}

// Must match PLAN_LIMITS['free'] in firebase/functions/access_control.py
const DEFAULT_LIMITS = {
  chat_messages: 3,
  own_maps: 1,
  other_maps: 0,
  analysis_type: 'summary',
};

const DEFAULT_USAGE = {
  chat_messages: 0,
  own_maps: 0,
  other_maps: 0,
};

function deserializeLimits(limits) {
  if (!limits) return DEFAULT_LIMITS;
  const result = { ...limits };
  for (const key of Object.keys(result)) {
    if (result[key] === -1) result[key] = Infinity;
  }
  return result;
}

export function PlanProvider({ children }) {
  const { user } = useAuth();
  const [plan, setPlan] = useState('free');
  const [limits, setLimits] = useState(DEFAULT_LIMITS);
  const [usage, setUsage] = useState(DEFAULT_USAGE);
  const [period, setPeriod] = useState(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchPlan = useCallback(async () => {
    if (!user) {
      setPlan('free');
      setLimits(DEFAULT_LIMITS);
      setUsage(DEFAULT_USAGE);
      setPeriod(null);
      setLoading(false);
      return;
    }

    try {
      const getUserPlanFn = httpsCallable(functions, 'get_user_plan');
      const result = await getUserPlanFn();
      const data = result.data;

      setPlan(data.plan || 'free');
      setLimits(deserializeLimits(data.limits));
      setUsage(data.usage || DEFAULT_USAGE);
      setPeriod(data.period || null);
      setHasSubscription(data.hasSubscription ?? false);
    } catch (error) {
      console.error('Error fetching user plan:', error);
      setPlan('free');
      setLimits(DEFAULT_LIMITS);
      setUsage(DEFAULT_USAGE);
      setHasSubscription(false);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  const refreshPlan = useCallback(() => {
    setLoading(true);
    return fetchPlan();
  }, [fetchPlan]);

  const isAtLimit = useCallback((feature) => {
    const limit = limits[feature];
    const used = usage[feature];
    if (limit === undefined || used === undefined) return false;
    if (limit === Infinity) return false;
    return used >= limit;
  }, [limits, usage]);

  return (
    <PlanContext.Provider value={{
      plan,
      limits,
      usage,
      period,
      hasSubscription,
      loading,
      refreshPlan,
      isAtLimit,
    }}>
      {children}
    </PlanContext.Provider>
  );
}
